import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocale } from 'src/lib/hooks/useLocal'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import Select from '@mui/material/Select'
import { MenuItem } from '@mui/material'
import {
  getProvincesByCountryCode,
  gstRegistrationTypes,
  getAddressFields,
  businessDetailsConfig,
  getDetailsByZip
} from 'src/lib/utils/onboarding'
import { GSTRegistrationType } from 'src/lib/types/onboarding'
import SectionTitle from 'src/components/common/SectionTitle'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import {
  AdditionalPocContactErrorsType,
  AdditionalPocContactKeyType,
  AdditionalPocContactType,
  OrganizationDetailsFormType,
  StateSelectorType
} from 'src/lib/types/settings'
import useForm, { ValidationSchema } from 'src/lib/hooks/useForm'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import { capitalizeFirstCharacter, scrollToElement, truncateString } from 'src/lib/utils/helpers'
import { BillingDetailsPayloadType } from 'src/lib/types/address'
import FileUploaderWithActions from 'src/components/common/form-elements/FileUploaderWithActions'
import { getRequest, patchRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useOnboarding } from 'src/lib/contexts/OnboardingContext'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { DownloadSimple, Trash as Trash2, Plus, Check } from '@phosphor-icons/react'
import PhoneNumberInputField from 'src/components/common/form-elements/PhoneNumberInputField'
import FileUploader from 'src/components/common/form-elements/FileUploader'
import { FileDataType } from 'src/lib/types/random'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import AdditionalPocContact from './AdditionalPocContact'
import {
  createNewPocContact,
  parseAdditionalPocContacts,
  parseAdditionalPocContactsForBackend,
  parsePrimaryPocContact,
  scrollIntoAdditionalPocErrors,
  validateField
} from 'src/lib/utils/settings'
import 'react-phone-input-2/lib/style.css'
import { useUser } from 'src/lib/contexts/UserContext'

import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { getTaxPayload } from 'src/lib/utils/OrganisationHelpers'

interface OrganizationDetailsFormProps {
  isConfigured: boolean
  updateStep?: any
}

const OrganizationDetailsForm: React.FC<OrganizationDetailsFormProps> = ({ updateStep }) => {
  const { sendActivityLog } = useActivityLogs()
  const { t } = useLocale()
  const { setLoaderProperties } = useLoader()

  const {
    getters: { merchant }
  } = useMerchant()
  const { onboarding, setOnboardingData } = useOnboarding()
  const [states, setStates] = useState<any>([])
  const [taxCategories, setTaxCategories] = useState<any>([])

  const [uploadedFileFormData, setUploadedFileFormData] = useState<any>(null)
  const [fileUploadError, setFileUploadError] = useState<string>('')
  const [isEditable, setIsEditable] = useState<boolean>(false)
  const [isShippingSame, setIsShippingSame] = useState<boolean>(true)
  const gstUploadRef = useRef<HTMLSpanElement>(null)
  const { user } = useUser()

  const inputStyle = { style: { fontSize: '14px' } }
  const dropdownStyle = {
    fontWeight: 400,
    fontSize: 14
  }

  useEffect(() => {
    if (Boolean(merchant?.accountId)) {
      getOrganizationDetails()
    }
  }, [merchant])

  useEffect(() => {
    getTaxCategories()
    getStates()
  }, [])

  const validationSchema: ValidationSchema = useMemo(() => {
    const schema = getAddressFields(businessDetailsConfig, states)
    return schema
  }, [states])

  const initialState: OrganizationDetailsFormType = {
    tax_type: onboarding?.taxDetails?.entity_type ?? '',
    identification_number: onboarding?.taxDetails?.identification_number ?? '',
    business_legal_name: onboarding?.taxDetails?.business_legal_name ?? '',
    business_trade_name: onboarding?.taxDetails?.business_trade_name ?? '',
    gst_business_name: onboarding?.taxDetails?.entity_name ?? '',
    billing_zip: onboarding?.taxDetails?.billing_address?.zip ?? '',
    billing_address1: onboarding?.taxDetails?.billing_address?.address1 ?? '',
    billing_address2: onboarding?.taxDetails?.billing_address?.address2 ?? '',
    billing_city: onboarding?.taxDetails?.billing_address?.city ?? '',
    billing_state: onboarding?.taxDetails?.billing_address?.state ?? '',
    billing_state_code: onboarding?.taxDetails?.billing_address?.state_code ?? '',
    billing_country: onboarding?.taxDetails?.billing_address?.country ?? 'India',
    billing_country_code: onboarding?.taxDetails?.billing_address?.country_code ?? 'IN',
    shipping_zip: onboarding?.taxDetails?.shipping_address?.zip ?? '',
    shipping_address1: onboarding?.taxDetails?.shipping_address?.address1 ?? '',
    shipping_address2: onboarding?.taxDetails?.shipping_address?.address2 ?? '',
    shipping_city: onboarding?.taxDetails?.shipping_address?.city ?? '',
    shipping_state: onboarding?.taxDetails?.shipping_address?.state ?? '',
    shipping_state_code: onboarding?.taxDetails?.shipping_address?.state_code ?? '',
    shipping_country: onboarding?.taxDetails?.shipping_address?.country ?? 'India',
    shipping_country_code: onboarding?.taxDetails?.shipping_address?.country_code ?? 'IN',
    are_addresses_same: onboarding?.taxDetails?.are_addresses_same ?? false,
    poc_email_id: onboarding?.taxDetails?.poc_email_id ?? '',
    poc_phone_number: onboarding?.taxDetails?.poc_phone_number ?? '',
    poc_name: onboarding?.taxDetails?.poc_name ?? '',
    category: onboarding?.taxDetails?.category ?? 'F_AND_B',
    gstFileUrl: ''
  }

  const {
    handleSubmit,
    inputProps,
    handleFieldChange,
    setValues,
    setErrors,
    state: { values, errors }
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useForm({ initialState, validationSchema })

  const scrollIntoErrors = (errors: any, pocContactsErrors: AdditionalPocContactErrorsType[]) => {
    const errorIdMap: { [key: string]: string } = {
      poc_name: 'poc_name',
      poc_email_id: 'poc_email_id',
      poc_phone_number: 'poc_phone_number',
      category: 'category',
      tax_type: 'tax_type',
      identification_number: 'gstin',
      business_legal_name: 'business_legal_name',
      business_trade_name: 'business_trade_name',
      billing_address1: 'address_line1',
      billing_zip: 'pincode',
      ...(isShippingSame ? {} : { shipping_address1: 'shipping_address_line1' }),
      ...(isShippingSame ? {} : { shipping_zip: 'shipping_pincode' })
    }

    for (const key in errorIdMap) {
      if (errors[key]?.status) {
        const element = document.getElementById(errorIdMap[key])
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          element.focus({ preventScroll: true })
          break
        }
      }
      if (key === 'poc_phone_number') {
        const element = scrollIntoAdditionalPocErrors(pocContactsErrors)
        if (element) {
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          element?.focus({ preventScroll: true })
          break
        }
      }
    }
  }

  const getOrganizationDetails = async () => {
    try {
      const response: any = await getRequest(`/organization/${merchant?.accountId}`, apiURI.AUTH_ENGINE)
      setOnboardingData(response?.data?.data)
      const taxDetails = response?.data?.data?.tax_details
      const pocContacts = response?.data?.data?.tax_details?.poc_details
      const primaryPocContact = pocContacts?.find((contact: any) => contact.primary === true)
      const additionalPocContacts = pocContacts?.filter((contact: any) => contact.primary !== true)
      await getStates()
      await getTaxCategories()
      setValues((oldValues: any) => ({
        ...oldValues,
        tax_type: taxDetails?.entity_type ?? '',
        identification_number: taxDetails?.identification_number ?? '',
        business_legal_name: taxDetails?.business_legal_name ?? '',
        business_trade_name: taxDetails?.business_trade_name ?? '',
        gst_business_name: taxDetails?.entity_name ?? '',
        billing_zip: taxDetails?.billing_address?.zip ?? '',
        billing_address1: taxDetails?.billing_address?.address1 ?? '',
        billing_address2: taxDetails?.billing_address?.address2 ?? '',
        billing_city: taxDetails?.billing_address?.city ?? '',
        billing_state: taxDetails?.billing_address?.state ?? '',
        billing_state_code: taxDetails?.billing_address?.state_code ?? '',
        billing_country: taxDetails?.billing_address?.country ?? 'India',
        billing_country_code: taxDetails?.billing_address?.country_code ?? 'IN',
        shipping_zip: taxDetails?.shipping_address?.zip ?? '',
        shipping_address1: taxDetails?.shipping_address?.address1 ?? '',
        shipping_address2: taxDetails?.shipping_address?.address2 ?? '',
        shipping_city: taxDetails?.shipping_address?.city ?? '',
        shipping_state: taxDetails?.shipping_address?.state ?? '',
        shipping_state_code: taxDetails?.shipping_address?.state_code ?? '',
        shipping_country: taxDetails?.shipping_address?.country ?? 'India',
        shipping_country_code: taxDetails?.shipping_address?.country_code ?? 'IN',
        are_addresses_same: taxDetails?.are_addresses_same ?? false,
        poc_email_id: primaryPocContact?.email ?? '',
        poc_phone_number: parsePrimaryPocContact(primaryPocContact?.phone) ?? '',
        poc_name: primaryPocContact?.name ?? '',
        category: taxDetails?.category ?? '',
        gstFileUrl: taxDetails?.gst_certificate_file_key ?? ''
      }))
      setAdditionalPocContacts(parseAdditionalPocContacts(additionalPocContacts))
    } catch (error: any) {
      console.error(error)
    }
  }

  const getStates = async () => {
    try {
      const response: any = await getProvincesByCountryCode('IN')
      if (!Boolean(response?.data)) return
      const data = response.data.map((data: any) => {
        const state: StateSelectorType = { name: data.name, value: data.code }
        return state
      })
      setStates(data)
    } catch (error) {
      console.error(error)
    }
  }

  const handleShippingDetails = (isShippingSame: boolean) => {
    if (isShippingSame) {
      setValues((oldValues: any) => ({
        ...oldValues,
        shipping_address1: oldValues.billing_address1,
        shipping_address2: oldValues.billing_address2,
        shipping_city: oldValues.billing_city,
        shipping_state: oldValues.billing_state,
        shipping_state_code: oldValues.billing_state_code,
        shipping_country: oldValues.billing_country,
        shipping_country_code: oldValues.billing_country_code,
        shipping_zip: oldValues.billing_zip
      }))
    }
    setIsShippingSame(isShippingSame)
  }

  const handleZipChange = async (zip: string, type: 'shipping' | 'billing') => {
    const countryCode = values?.billing_country_code

    if (isShippingSame) {
      handleFieldChange('billing_zip', zip)
      handleFieldChange('shipping_zip', zip)
    } else {
      const zipField = type === 'shipping' ? 'shipping_zip' : 'billing_zip'
      handleFieldChange(zipField, zip)
    }

    let zipDetails: any = {}
    try {
      if (Boolean(zip) && countryCode === 'IN' && zip?.length === 6) {
        zipDetails = await getDetailsByZip(zip)
        if (type === 'shipping') {
          setValues((oldValues: any) => ({
            ...oldValues,
            shipping_city: capitalizeFirstCharacter(zipDetails.district),
            shipping_state: capitalizeFirstCharacter(zipDetails.state),
            shipping_state_code: zipDetails.state_code
          }))
        } else {
          setValues((oldValues: any) => ({
            ...oldValues,
            billing_city: capitalizeFirstCharacter(zipDetails.district),
            billing_state: capitalizeFirstCharacter(zipDetails.state),
            billing_state_code: zipDetails.state_code,
            ...(isShippingSame && {
              shipping_city: capitalizeFirstCharacter(zipDetails.district),
              shipping_state: capitalizeFirstCharacter(zipDetails.state),
              shipping_state_code: zipDetails.state_code
            })
          }))
        }

        setErrors((oldValues: any) => ({
          ...oldValues,
          billing_city: { ...oldValues.city, status: false },
          billing_state: { ...oldValues.state, status: false },
          billing_state_code: { ...oldValues.state_code, status: false }
        }))
      }
    } catch (error: any) {
      console.error(error)
    }
  }

  const onGSTFormSubmit = async () => {
    const primaryPocContact = {
      name: values?.poc_name,
      email: values?.poc_email_id,
      phone: values?.poc_phone_number,
      designation: 'Primary Finance POC',
      primary: true
    }

    const taxpayload: BillingDetailsPayloadType = getTaxPayload({
      values,
      additionalPocContacts,
      isShippingSame
    })

    try {
      setLoaderProperties({ isLoaderVisible: true, message: 'Updating organization details...' })

      if (uploadedFileFormData === 'DELETED') {
        scrollToElement(gstUploadRef)
        setFileUploadError('Please upload a GST certificate')
        return
      }

      if (
        values?.tax_type !== 'UNREGISTERED_BUSINESS' &&
        !Boolean(uploadedFileFormData) &&
        !Boolean(values?.gstFileUrl)
      ) {
        scrollToElement(gstUploadRef)
        setFileUploadError('Please upload a GST certificate')
        return
      }
      setFileUploadError('')

      if (!uploadedFileFormData) {
        await patchRequest(`/organization/${merchant?.accountId}/tax-details`, apiURI.AUTH_ENGINE, taxpayload)
        successToast(t('alert_customization_saved'))
      } else {
        const taxPayloadData = new FormData()
        taxPayloadData.append('file', uploadedFileFormData.get('file'))
        taxPayloadData.append('request', JSON.stringify(taxpayload))
        await patchRequest(`/organization/${merchant?.accountId}/tax-details`, apiURI.AUTH_ENGINE, taxPayloadData)
        successToast(t('alert_customization_saved'))
      }
      getOrganizationDetails()
      setIsEditable(false)
    } catch (error: any) {
      errorToast(error?.response?.data?.error?.message)
      console.error(error)
    } finally {
      setLoaderProperties({ isLoaderVisible: false, message: '' })
    }
  }
  const getTaxCategories = async () => {
    try {
      const response: any = await getRequest('/utility/organization-categories', apiURI.AUTH_ENGINE)
      const data = response?.data?.data
      if (!Array.isArray(data)) return
      setTaxCategories(data)
    } catch (error: any) {
      console.error(error)
    }
  }
  const [additionalPocContacts, setAdditionalPocContacts] = useState<AdditionalPocContactType[]>([])
  const [isSaveClicked, setIsSaveClicked] = useState(false)

  const handleClick = () => {
    if (!isEditable) {
      setIsEditable(true)
    } else {
      const additionalPocContactsErrors = validateAdditionalPocContacts()
      handleSubmit(onGSTFormSubmit, scrollIntoErrors, additionalPocContactsErrors)
    }
  }

  const validateAdditionalPocContacts = () => {
    setIsSaveClicked(true)

    const newAdditionalPocContacts = additionalPocContacts.map(contact => {
      const errors: AdditionalPocContactErrorsType = {
        name: validateField('name', contact.values.name),
        email: validateField('email', contact.values.email),
        phone: validateField('phone', contact.values.phone),
        designation: validateField('designation', contact.values.designation)
      }
      return {
        ...contact,
        errors
      }
    })
    setAdditionalPocContacts(newAdditionalPocContacts)

    const hasAdditionalPocContactsErrors = newAdditionalPocContacts.some(contact =>
      Object.values(contact.errors).some(error => error?.status)
    )
    const activityLogPayload = {
      entityName: EntityName.SETTINGS,
      actionName: 'edited Setup'
    }
    sendActivityLog(activityLogPayload)
    return {
      hasPocContactErrors: hasAdditionalPocContactsErrors,
      pocContactsErrors: newAdditionalPocContacts.map(contact => contact.errors)
    }
  }
  const handleAdditionalPocContacts = (index: number, fieldName: AdditionalPocContactKeyType, value: string) => {
    const newAdditionalPocContacts = [...additionalPocContacts]
    newAdditionalPocContacts[index].values[fieldName] = value
    if (isSaveClicked) {
      const error = validateField(fieldName, value.toString())
      newAdditionalPocContacts[index].errors[fieldName] = error
    }
    setAdditionalPocContacts(newAdditionalPocContacts)
  }

  useEffect(() => {
    if (!additionalPocContacts[additionalPocContacts.length - 1]?.values?.name) {
      const newElementNameField = document.getElementById(`additional_poc_name_${additionalPocContacts.length - 1}`)
      newElementNameField?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      newElementNameField?.focus({ preventScroll: true })
    }
  }, [additionalPocContacts.length])

  const handleNewPocContact = () => {
    const newPocContact = createNewPocContact()
    setAdditionalPocContacts([...additionalPocContacts, newPocContact])
  }

  const deleteAdditionalPOC = (index: number) => {
    const newAdditionalPocContacts = [...additionalPocContacts]
    newAdditionalPocContacts.splice(index, 1)
    setAdditionalPocContacts(newAdditionalPocContacts)
  }

  return (
    <div>
      <button
        disabled={user?.isDemoUser}
        onClick={handleClick}
        className='-mt-9 mb-4 flex items-center ml-auto text-white justify-center px-3 py-2 text-sm font-semibold rounded-lg bg-primary-dark disabled:bg-[#E0E0E0]'
      >
        <p>{isEditable ? t('save') : t('edit')}</p>
      </button>
      <form className='flex flex-col gap-4' id='business-details-form'>
        {/* POC form */}
        <div className='flex flex-col border w-full bg-white rounded-lg gap-4 py-4'>
          <div className=' px-4'>
            <h2 className='font-medium text-carbon'>Point of contact for billing (finance POC) </h2>
          </div>
          <div className={`grid grid-cols-2 gap-5 px-4`}>
            {Boolean(validationSchema?.poc_name) && (
              <InputTextField
                label={t('poc_name')}
                inputProps={inputStyle}
                textFieldId='poc_name'
                errorStatus={errors?.poc_name?.status}
                errorMessage={errors?.poc_name?.message}
                customErrorMessage='Please enter at least 3 character in your name'
                handleChange={(e: any) => handleFieldChange('poc_name', e.target.value)}
                inputType='text'
                inputValue={values?.poc_name}
                isDisabled={!Boolean(isEditable)}
                minCharacterLimit={3}
                isRequired={true}
                toolTipRequired={false}
              />
            )}
            <InputTextField
              label={t('poc_designation')}
              inputProps={inputStyle}
              textFieldId='poc_designation'
              inputType='text'
              inputValue='Primary Finance POC'
              isDisabled={true}
              isRequired={false}
            />
            {Boolean(validationSchema?.poc_email_id) && (
              <InputTextField
                label={t('poc_email_id')}
                inputProps={inputStyle}
                textFieldId='poc_email_id'
                errorStatus={errors?.poc_email_id?.status}
                errorMessage={errors?.poc_email_id?.message}
                handleChange={(e: any) => handleFieldChange('poc_email_id', e.target.value)}
                inputType='text'
                inputValue={values?.poc_email_id}
                isDisabled={!Boolean(isEditable)}
                isRequired={true}
                toolTipRequired={false}
              />
            )}
            {Boolean(validationSchema?.poc_phone_number) && (
              <PhoneNumberInputField
                label={t('poc_phone_number')}
                errorStatus={errors?.poc_phone_number?.status}
                errorMessage={errors?.poc_phone_number?.message}
                handleChange={(e: any) => handleFieldChange('poc_phone_number', e)}
                inputValue={values?.poc_phone_number}
                isDisabled={!Boolean(isEditable)}
                isRequired={true}
                onlyCountries={['in']}
                id='poc_phone_number'
              />
            )}
          </div>

          {isEditable && (
            <div className='flex flex-wrap justify-end gap-4 px-4 border-t-1 border-coal-lighter pt-4'>
              <button
                className='flex items-center gap-2 px-3  py-2 font-semibold bg-white border rounded-xl border-flo-gray-light text-coal-dark w-fit disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handleNewPocContact}
                disabled={additionalPocContacts.length >= 3}
                type='button'
              >
                <Plus size='20' />
                <span>Additional contacts</span>
              </button>
            </div>
          )}

          {additionalPocContacts.map((contact, index) => (
            <AdditionalPocContact
              key={index}
              errors={contact.errors}
              values={contact.values}
              isEditable={isEditable}
              handleFieldChange={handleAdditionalPocContacts}
              index={index}
              onDeleteAdditionalPOC={deleteAdditionalPOC}
            />
          ))}
        </div>
        {/* POC form */}
        {/* GST Form */}
        <div className='flex flex-col border w-full bg-[#FFFFFF] rounded-lg p-4 gap-4'>
          <div>
            <h2 className='font-medium text-carbon'>Organisation</h2>
          </div>
          {Boolean(validationSchema?.category) && (
            <div>
              <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
                <span>
                  {t('category')}
                  <span style={{ color: '#cb2711' }}>*</span>
                </span>
              </label>
              <Select
                labelId='mov-select-label'
                inputProps={inputStyle}
                className='w-full h-10'
                sx={{ ...dropdownStyle, padding: 0 }}
                placeholder='Select one'
                {...inputProps('category')}
                error={errors?.category?.status}
                disabled={!Boolean(isEditable)}
              >
                {taxCategories?.map((data: any) => (
                  <MenuItem key={data?.id} value={data?.id} sx={dropdownStyle}>
                    {data?.display_name}
                  </MenuItem>
                ))}
              </Select>
              {errors?.category?.status && <TextFieldError message={errors?.category?.message ?? ''} />}
            </div>
          )}
          {Boolean(validationSchema?.tax_type) && (
            <div>
              <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
                <span>
                  {t('tax_type')}
                  <span style={{ color: '#cb2711' }}>*</span>
                </span>
              </label>
              <Select
                labelId='mov-select-label'
                inputProps={inputStyle}
                className='w-full h-10'
                sx={{ ...dropdownStyle, padding: 0 }}
                placeholder='Select one'
                {...inputProps('tax_type')}
                error={errors?.tax_type?.status}
                disabled={!Boolean(isEditable)}
              >
                {gstRegistrationTypes?.map((data: GSTRegistrationType) => (
                  <MenuItem key={data?.title} value={data?.value} sx={dropdownStyle}>
                    {data.title}
                  </MenuItem>
                ))}
              </Select>
              {errors?.tax_type?.status && <TextFieldError message={errors?.tax_type?.message ?? ''} />}
            </div>
          )}
          {!Boolean(values?.tax_type === 'UNREGISTERED_BUSINESS') &&
            Boolean(validationSchema?.identification_number) && (
              <InputTextField
                label={t('gstin')}
                inputProps={inputStyle}
                textFieldId='gstin'
                errorStatus={errors?.identification_number?.status}
                errorMessage={errors?.identification_number?.message}
                handleChange={(e: any) => handleFieldChange('identification_number', e.target.value)}
                inputType='text'
                customErrorMessage={'Please enter 15 character GSTIN number'}
                inputValue={values?.identification_number}
                isDisabled={!Boolean(isEditable)}
                fixedCharacterLimit={15}
                isRequired={!Boolean(values?.tax_type === 'UNREGISTERED_BUSINESS')}
                toolTipRequired={false}
              />
            )}
          {Boolean(validationSchema?.business_legal_name) && (
            <InputTextField
              label={t('business_legal_name')}
              inputProps={inputStyle}
              textFieldId='business_legal_name'
              inputType='text'
              minCharacterLimit={3}
              customErrorMessage='Please enter a legal business name with at least 3 characters'
              errorStatus={errors?.business_legal_name?.status}
              errorMessage={errors?.business_legal_name?.message}
              handleChange={(e: any) => handleFieldChange('business_legal_name', e.target.value)}
              inputValue={values?.business_legal_name}
              isDisabled={!Boolean(isEditable)}
              isRequired={true}
              toolTipRequired={false}
            />
          )}
          {Boolean(validationSchema?.business_trade_name) && (
            <InputTextField
              label={t('business_trade_name')}
              inputProps={inputStyle}
              textFieldId='business_trade_name'
              inputType='text'
              minCharacterLimit={3}
              customErrorMessage='Please enter a business trade name with at least 3 characters'
              errorStatus={errors?.business_trade_name?.status}
              errorMessage={errors?.business_trade_name?.message}
              handleChange={(e: any) => {
                handleFieldChange('business_trade_name', e.target.value)
              }}
              inputValue={values?.business_trade_name}
              isDisabled={!Boolean(isEditable)}
              isRequired={true}
              toolTipRequired={false}
            />
          )}
          <>
            {Boolean(validationSchema?.gst_business_name) && (
              <InputTextField
                label={values?.tax_type === 'UNREGISTERED_BUSINESS' ? t('business_name') : t('gst_business_name')}
                inputProps={inputStyle}
                textFieldId='gst_business_name'
                handleChange={(e: any) => handleFieldChange('gst_business_name', e.target.value)}
                inputType='text'
                inputValue={values?.gst_business_name}
                isDisabled={!Boolean(isEditable)}
                isRequired={false}
                toolTipRequired={false}
              />
            )}
            {Boolean(validationSchema?.billing_address1) && (
              <InputTextField
                label={t('address_line1')}
                inputProps={inputStyle}
                textFieldId='address_line1'
                handleChange={(e: any) => {
                  if (isShippingSame) {
                    handleFieldChange('shipping_address1', e.target.value)
                  }
                  handleFieldChange('billing_address1', e.target.value)
                }}
                fixedCharacterLimit={100}
                showCharacterCountConfig={{
                  maxLength: 100,
                  showOnlyOnFocus: false,
                  customClass: 'text-xs',
                  showCharacterCountOnLabel: true
                }}
                customErrorMessage=''
                inputType='text'
                errorStatus={errors?.billing_address1?.status}
                errorMessage={errors?.billing_address1?.message}
                inputValue={(values?.billing_address1 ?? '').slice(0, 100)}
                isDisabled={!Boolean(isEditable)}
                isRequired={true}
                toolTipRequired={false}
              />
            )}
            {Boolean(validationSchema?.billing_address2) && (
              <InputTextField
                label={t('address_line2')}
                inputProps={inputStyle}
                textFieldId='address_line2'
                fixedCharacterLimit={100}
                handleChange={(e: any) => {
                  if (isShippingSame) {
                    handleFieldChange('shipping_address2', e.target.value)
                  }
                  handleFieldChange(`billing_address2`, e.target.value)
                }}
                showCharacterCountConfig={{
                  maxLength: 100,
                  showOnlyOnFocus: false,
                  customClass: 'text-xs',
                  showCharacterCountOnLabel: true
                }}
                inputType='text'
                errorStatus={errors?.billing_address2?.status}
                errorMessage={errors?.billing_address2?.message}
                inputValue={(values?.billing_address2 ?? '').slice(0, 100)}
                isDisabled={!Boolean(isEditable)}
                customErrorMessage=''
              />
            )}
            {Boolean(validationSchema?.billing_zip) && (
              <InputTextField
                label={t('pincode')}
                inputProps={inputStyle}
                textFieldId='pincode'
                handleChange={(e: any) => {
                  handleZipChange(e.target.value, 'billing')
                }}
                fixedNumberLimit={6}
                customErrorMessage='Please enter a valid pincode'
                inputType='number'
                errorStatus={errors?.billing_zip?.status}
                errorMessage={errors?.billing_zip?.message}
                inputValue={values?.billing_zip}
                isDisabled={!Boolean(isEditable)}
                isRequired={true}
                toolTipRequired={false}
              />
            )}
            {Boolean(validationSchema?.billing_city) && (
              <InputTextField
                label={t('city')}
                inputProps={inputStyle}
                textFieldId='city'
                handleChange={(e: any) => {
                  if (isShippingSame) {
                    handleFieldChange('shipping_city', e.target.value)
                  }
                  handleFieldChange(`billing_city`, e.target.value)
                }}
                inputType='text'
                errorStatus={errors?.billing_city?.status}
                errorMessage={errors?.billing_city?.message}
                inputValue={values?.billing_city}
                isDisabled={true}
                isRequired={true}
                toolTipRequired={false}
              />
            )}
            {!Boolean(isEditable) ? (
              <InputTextField
                label={t('state')}
                inputProps={inputStyle}
                textFieldId='state'
                handleChange={(e: any) => {
                  if (isShippingSame) {
                    handleFieldChange('shipping_state', e.target.value)
                  }
                  handleFieldChange(`billing_state`, e.target.value)
                }}
                inputType='text'
                inputValue={values?.billing_state}
                isDisabled={true}
                isRequired={true}
                toolTipRequired={false}
              />
            ) : (
              <>
                {Boolean(validationSchema?.billing_state) && (
                  <div className=''>
                    <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
                      <span>
                        {t('state')}
                        <span style={{ color: '#cb2711' }}>*</span>
                      </span>
                    </label>
                    <Select
                      labelId='mov-select-label'
                      id='mov-select'
                      onChange={(e: any) => {
                        const stateName = states.find(
                          (state: StateSelectorType) => state.value === e.target.value
                        )?.name
                        setValues((oldValues: any) => ({
                          ...oldValues,
                          billing_state: capitalizeFirstCharacter(stateName),
                          billing_state_code: e.target.value,
                          ...(isShippingSame && {
                            shipping_state: capitalizeFirstCharacter(stateName),
                            shipping_state_code: e.target.value
                          })
                        }))
                      }}
                      inputProps={inputStyle}
                      className='w-full h-10'
                      sx={{ ...dropdownStyle, padding: 0 }}
                      placeholder='Select one'
                      error={errors?.billing_state?.status}
                      disabled={true}
                      value={values?.billing_state_code}
                    >
                      {states?.map((data: StateSelectorType) => (
                        <MenuItem key={data?.value} value={data?.value} sx={dropdownStyle}>
                          <>{data?.name}</>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors?.billing_state?.status && <TextFieldError message={errors?.billing_state?.message ?? ''} />}
                  </div>
                )}
              </>
            )}

            {Boolean(validationSchema?.billing_country) && (
              <InputTextField
                label={t('country')}
                inputProps={inputStyle}
                textFieldId='country'
                inputType='text'
                errorStatus={errors?.billing_country?.status}
                errorMessage={errors?.billing_country?.message}
                isDisabled={true}
                inputValue={initialState.billing_country}
                isRequired={true}
                toolTipRequired={false}
              />
            )}
          </>
          <SectionTitle title={t('shipping_address')} customClass={`uppercase !text-xxs -mx-4 `} />
          <FloCheckbox
            value={isShippingSame}
            setValue={handleShippingDetails}
            label={t('same_as_billing')}
            isDisabled={!Boolean(isEditable)}
          />
          {!Boolean(isShippingSame) && (
            <>
              {Boolean(validationSchema?.shipping_address1) && (
                <InputTextField
                  label={t('address_line1')}
                  inputProps={inputStyle}
                  textFieldId='shipping_address_line1'
                  handleChange={(e: any) => handleFieldChange('shipping_address1', e.target.value)}
                  inputType='text'
                  errorStatus={errors?.shipping_address1?.status}
                  customErrorMessage=''
                  fixedCharacterLimit={100}
                  errorMessage={errors?.shipping_address1?.message}
                  inputValue={(values?.shipping_address1 ?? '').slice(0, 100)}
                  showCharacterCountConfig={{
                    maxLength: 100,
                    showOnlyOnFocus: false,
                    customClass: 'text-xs',
                    showCharacterCountOnLabel: true
                  }}
                  isDisabled={!Boolean(isEditable)}
                  isRequired={true}
                  toolTipRequired={false}
                />
              )}
              {Boolean(validationSchema?.shipping_address2) && (
                <InputTextField
                  label={t('address_line2')}
                  inputProps={inputStyle}
                  textFieldId='shipping_address_line2'
                  handleChange={(e: any) => handleFieldChange(`shipping_address2`, e.target.value)}
                  inputType='text'
                  errorStatus={errors?.shipping_address2?.status}
                  errorMessage={errors?.shipping_address2?.message}
                  inputValue={(values?.shipping_address2 ?? '').slice(0, 100)}
                  isDisabled={!Boolean(isEditable)}
                  customErrorMessage=''
                  fixedCharacterLimit={100}
                  showCharacterCountConfig={{
                    maxLength: 100,
                    showOnlyOnFocus: false,
                    customClass: 'text-xs',
                    showCharacterCountOnLabel: true
                  }}
                />
              )}
              {Boolean(validationSchema?.shipping_zip) && (
                <InputTextField
                  label={t('pincode')}
                  inputProps={inputStyle}
                  textFieldId='shipping_pincode'
                  handleChange={(e: any) => {
                    handleZipChange(e.target.value, 'shipping')
                  }}
                  fixedNumberLimit={6}
                  customErrorMessage='Please enter a valid pincode'
                  inputType='number'
                  errorStatus={errors?.shipping_zip?.status}
                  errorMessage={errors?.shipping_zip?.message}
                  inputValue={values?.shipping_zip}
                  isDisabled={!Boolean(isEditable)}
                  isRequired={true}
                  toolTipRequired={false}
                />
              )}
              {Boolean(validationSchema?.shipping_city) && (
                <InputTextField
                  label={t('city')}
                  inputProps={inputStyle}
                  textFieldId='shipping_city'
                  handleChange={(e: any) => handleFieldChange(`shipping_city`, e.target.value)}
                  inputType='text'
                  errorStatus={errors?.shipping_city?.status}
                  errorMessage={errors?.shipping_city?.message}
                  inputValue={values?.shipping_city}
                  isDisabled={!Boolean(isEditable)}
                  isRequired={true}
                  toolTipRequired={false}
                />
              )}

              {Boolean(validationSchema?.shipping_state) && (
                <div className=''>
                  <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
                    <span>
                      {t('state')}
                      <span style={{ color: '#cb2711' }}>*</span>
                    </span>
                  </label>
                  <Select
                    labelId='mov-select-label'
                    id='mov-select'
                    onChange={(e: any) => {
                      const stateName = states.find((state: StateSelectorType) => state.value === e.target.value)?.name
                      setValues((oldValues: any) => ({
                        ...oldValues,
                        shipping_state: capitalizeFirstCharacter(stateName),
                        shipping_state_code: e.target.value
                      }))

                      setErrors((oldValues: any) => ({
                        ...oldValues,
                        shipping_state: { ...oldValues.state, status: false },
                        shipping_state_code: { ...oldValues.state_code, status: false }
                      }))
                    }}
                    inputProps={inputStyle}
                    className='w-full h-10'
                    sx={{ ...dropdownStyle, padding: 0 }}
                    placeholder='Select one'
                    error={errors?.shipping_state?.status}
                    disabled={!Boolean(isEditable)}
                    value={values?.shipping_state_code}
                  >
                    {states?.map((data: StateSelectorType) => (
                      <MenuItem key={data?.value} value={data?.value} sx={dropdownStyle}>
                        <>{data?.name}</>
                      </MenuItem>
                    ))}
                  </Select>

                  {errors?.shipping_state?.status && <TextFieldError message={errors?.shipping_state?.message ?? ''} />}
                </div>
              )}

              {Boolean(validationSchema?.shipping_country) && (
                <InputTextField
                  label={t('country')}
                  inputProps={inputStyle}
                  textFieldId='shipping_country'
                  inputType='text'
                  errorStatus={errors?.shipping_country?.status}
                  errorMessage={errors?.shipping_country?.message}
                  inputValue={initialState?.shipping_country}
                  isDisabled={true}
                  isRequired={true}
                  toolTipRequired={false}
                />
              )}
            </>
          )}

          {/* File Uploader */}
          {!Boolean(values?.tax_type === 'UNREGISTERED_BUSINESS') && (
            <FileUploaderWithActions
              buttonText={t('upload')}
              type='.pdf'
              size='medium'
              variant='outlined'
              acceptedSize={10485760}
              color='primary'
              uploadOnFileSave={false}
              placeholderText={
                <span ref={gstUploadRef}>
                  Upload your GST certificate<span style={{ color: '#cb2711' }}>*</span>
                </span>
              }
              setSelectedCSVFormData={file => {
                setUploadedFileFormData(file)
                setFileUploadError('')
              }}
              errorMessage={fileUploadError}
              errorStatus={Boolean(fileUploadError)}
              showDeleteButton={Boolean(isEditable)}
              disabled={Boolean(!isEditable)}
              prevUploadedFileURL={values?.gstFileUrl}
              prevUploadedFileName={values?.gstFileUrl ? 'gst.pdf' : ''}
            />
          )}
        </div>
      </form>
    </div>
  )
}

export default OrganizationDetailsForm
